import React, { useState, useEffect, useCallback } from 'react';
import { X, Phone, CheckCircle, Heart, Calendar, Users, Star, ArrowRight } from 'lucide-react';
import { sendToWhatsApp } from '../utils/whatsapp';
import { submitInquiry } from '../utils/leads';

const LeadCapturePopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  // Exit intent detection
  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 5 && !sessionStorage.getItem('leadPopupShown')) {
      setIsOpen(true);
      sessionStorage.setItem('leadPopupShown', 'true');
    }
  }, []);

  useEffect(() => {
    const popupShown = sessionStorage.getItem('leadPopupShown');
    if (popupShown) return;

    // Timer: show after 12 seconds
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem('leadPopupShown')) {
        setIsOpen(true);
        sessionStorage.setItem('leadPopupShown', 'true');
      }
    }, 12000);

    // Exit intent
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitInquiry({
        name: formData.name,
        phone: formData.phone,
        email: '',
        eventType: 'Wedding Consultation',
        message: 'Quick Lead Capture - Requested free consultation'
      }, 'lead-popup');
    } catch (error) {
      console.error('Error saving lead:', error);
    }
    sendToWhatsApp({
      name: formData.name,
      phone: formData.phone,
      message: 'Requested FREE Wedding Consultation from popup'
    }, 'Quick Consultation Request');
    setSubmitted(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="lead-capture-popup">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ animation: 'popupSlideIn 0.3s ease-out' }}>
        <style>{`
          @keyframes popupSlideIn { from { opacity: 0; transform: translateY(20px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>

        <button onClick={() => setIsOpen(false)} className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow-md" data-testid="popup-close-btn">
          <X className="h-4 w-4 text-gray-500" />
        </button>

        {!submitted ? (
          <div className="flex flex-col md:flex-row">
            {/* Left: Image + Overlay */}
            <div className="relative md:w-[45%] h-48 md:h-auto min-h-[280px] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_pretty-planet-travel/artifacts/c2co11fh_IMG_1458.JPG"
                alt="Luxury wedding in Dharamshala"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(36,24,14,0.8), rgba(36,24,14,0.3))' }} />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex -space-x-2">
                    {[1,2,3].map(i => (
                      <div key={i} className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold" style={{ background: `linear-gradient(135deg, #C9A96E, #B8943F)` }}>
                        {['R','S','P'][i-1]}
                      </div>
                    ))}
                  </div>
                  <span className="text-xs text-white/80">12 couples booked this month</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => <Star key={i} className="h-3.5 w-3.5 text-yellow-400" fill="currentColor" />)}
                  <span className="text-xs text-white/70 ml-1">5.0 (200+ reviews)</span>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="md:w-[55%] p-6 md:p-7">
              {/* Urgency badge */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-4" style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.15)' }}>
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs font-semibold text-red-600">Only 6 dates left for 2026 season</span>
              </div>

              <h2 className="text-xl md:text-2xl mb-1" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#2C2C2C' }}>
                Plan Your Dream
              </h2>
              <h2 className="text-xl md:text-2xl mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#C9A96E' }}>
                Himalayan Wedding
              </h2>
              <p className="text-gray-500 text-sm mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Get a <strong className="text-gray-700">FREE personalized quote</strong> from Dharamshala's top wedding planner
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  required
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] transition-colors"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  data-testid="popup-name-input"
                />
                <input
                  type="tel"
                  required
                  placeholder="WhatsApp Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] transition-colors"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  data-testid="popup-phone-input"
                />
                <button
                  type="submit"
                  className="w-full py-3.5 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #C9A96E 0%, #B8943F 100%)', boxShadow: '0 6px 20px rgba(201,169,110,0.35)', fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  data-testid="popup-submit-btn"
                >
                  Get Free Quote Now
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>

              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" style={{ color: '#C9A96E' }} />No spam</span>
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" style={{ color: '#C9A96E' }} />Reply in 2hrs</span>
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" style={{ color: '#C9A96E' }} />100% free</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 px-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#2C2C2C' }}>
              We'll Call You Soon!
            </h3>
            <p className="text-gray-500 text-sm mb-2">Please send the WhatsApp message to confirm.</p>
            <p className="text-sm font-semibold mb-6" style={{ color: '#C9A96E' }}>Our expert will reach out within 2 hours</p>
            <button onClick={() => setIsOpen(false)} className="px-8 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm" data-testid="popup-close-success-btn">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCapturePopup;
